import { draftContentFragment } from '.';
import noop from 'lodash/noop';
import React from 'react';
import ReactDiffViewer from 'react-diff-viewer';
import { useParams } from 'react-router-dom';

import { useCompleteFragment } from '@eluve/apollo-client';
import { H2, H5, HStack, Heading, VStack } from '@eluve/components';
import {
  Block,
  DynamicArtifactTemplate,
  dynamicArtifactTemplateSchema,
  flattenBlocks,
  hydrateDynamicArtifactTemplate,
} from '@eluve/llm-outputs';
import { SummarySectionMarkdown } from '@eluve/smart-blocks';
import {
  AppointmentSummaryKeys,
  summaryKeyToSectionTitleMap,
} from '@eluve/utils';

import { DiffExportButton } from './DiffExportButton';
import { DiffViewer } from './DiffViewer';

export const SummaryDiffContent: React.FC = () => {
  const { appointmentId, tenantId } = useParams() as {
    tenantId: string;
    appointmentId: string;
  };
  const appointment = useCompleteFragment({
    fragment: draftContentFragment,
    key: { id: appointmentId },
  });

  const humanSoapNote = appointment?.humanOutputs?.[0];
  const humanSoapNoteContent = humanSoapNote?.output?.content;
  const { additionalNotes } = appointment?.doctor_interaction ?? {};

  const llmSoapNoteContent = humanSoapNote?.output?.llm_output?.content;

  const outputVariant =
    humanSoapNote?.output?.llm_output?.metadata?.output_variant;

  let hydratedLlmSummary: DynamicArtifactTemplate | undefined = undefined;
  let hydratedHumanOutput: DynamicArtifactTemplate | undefined = undefined;
  let llmBlocks: Block[] | undefined;
  let humanBlocks: Block[] | undefined;
  if (outputVariant) {
    const artifactTemplate = dynamicArtifactTemplateSchema.parse(
      outputVariant.template,
    );
    hydratedLlmSummary = hydrateDynamicArtifactTemplate(
      artifactTemplate,
      llmSoapNoteContent ?? {},
    );
    hydratedHumanOutput = hydrateDynamicArtifactTemplate(
      artifactTemplate,
      humanSoapNoteContent ?? {},
    );
    llmBlocks = flattenBlocks(hydratedLlmSummary.blocks);
    humanBlocks = flattenBlocks(hydratedHumanOutput.blocks);
  }

  return (
    <VStack>
      <HStack className="mt-4" justify="between" align="center">
        <Heading>Summary</Heading>
        <DiffExportButton
          appointmentId={appointmentId}
          tenantId={tenantId}
          type="summary"
        />
      </HStack>
      {outputVariant ? (
        <VStack gap={8}>
          {(() => {
            if (!llmBlocks || !humanBlocks) {
              return null;
            }
            return llmBlocks.map((llmBlock) => {
              const matchingHumanBlock = humanBlocks.find(
                (b) => b.key === llmBlock.key,
              );
              return (
                <VStack key={llmBlock.label} align="stretch" gap={4}>
                  <H2>{llmBlock.label}</H2>
                  <DiffViewer
                    oldBlock={llmBlock}
                    newBlock={matchingHumanBlock!}
                  />
                </VStack>
              );
            });
          })()}
        </VStack>
      ) : (
        <VStack gap={8}>
          {Object.entries(summaryKeyToSectionTitleMap).map(
            ([key, sectionTitle]) => {
              const summaryKey = key as AppointmentSummaryKeys;
              return (
                <VStack key={summaryKey} align="stretch" gap={4}>
                  <H5>{sectionTitle}</H5>
                  <ReactDiffViewer
                    oldValue={llmSoapNoteContent?.[summaryKey]}
                    newValue={humanSoapNoteContent?.[summaryKey]}
                    splitView={true}
                    showDiffOnly={true}
                  />
                </VStack>
              );
            },
          )}
        </VStack>
      )}
      {additionalNotes && (
        <SummarySectionMarkdown
          key="AdditionalNotes"
          // TODO(jesse): this should get cleaned up and use a different component [ELU-949]
          summaryKey={`AdditionalNotes` as any}
          sectionTitle="Additional Notes"
          content={additionalNotes}
          disabled={true}
          handleContentChange={noop}
        />
      )}
    </VStack>
  );
};
